import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {Box, Divider, Stack, Tab, Tabs} from "@mui/material";
import Typography from "@mui/material/Typography";
import {GastrodatConfig} from "crqlar-react-common/dist/models/integrations/gastrodat/gastrodat.config";
import {GastrodatService} from "crqlar-react-common/dist/services/integrations/gastrodat/gastrodat.service";
import GastrodatConfigForm from "./GastrodatConfigForm";
import Logs from "./Logs";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {getDefaultAgeCategoryIds} from "src/utils/integrationDefaults.util";

const GastrodatConfigDetails = () => {

    useTrackScreenVisit('system gastrodat config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const gastrodatConfigData = useEntity<GastrodatConfig> ({
        get: async () => {
            return await GastrodatService.find()
        },
        create: GastrodatService.create,
        update: GastrodatService.update,
        delete: (id) => GastrodatService.delete({id: [id]}),
        isValid: (gastrodatConfig) => {
            return !!gastrodatConfig.name
        },
        cleanupBeforeSave: (gastrodatConfig) => {
            return {
                ...gastrodatConfig,
                name: gastrodatConfig.name?.trim()
            }
        }
    })

    const gastrodatConfig = gastrodatConfigData.item

    const handleCreate = async () => {
        const [sectors, defaultAgeCategoryIds] = await Promise.all([
            SectorService.getAllSectors({limit: {offset: 0, limit: 1000}}),
            getDefaultAgeCategoryIds()
        ])

        gastrodatConfigData.startCreate({
            enabled: true,
            processingEnabled: true,
            accommodationSectorId: sectors.content?.find(s => s.sectorType === SectorType.ACCOMMODATION)?.id,
            adultsCategoryId: defaultAgeCategoryIds.adultsAgeCategoryId,
            childrenCategoryId: defaultAgeCategoryIds.childrenAgeCategoryId,
        } as GastrodatConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={gastrodatConfig.name}
            data={gastrodatConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={gastrodatConfigData.saving}
                onDelete={() => gastrodatConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {gastrodatConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    gastrodatConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!gastrodatConfig.id &&
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        aria-label="tabs"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Tab label={t('Configuration')} value={'config'}/>
                        <Tab label={t('Stats and Logs')} value={'logs'}/>
                    </Tabs>
                }

                {
                    !gastrodatConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <GastrodatConfigForm gastrodatConfigData={gastrodatConfigData}/>

                            {
                                !!gastrodatConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Logs gastrodatConfigData={gastrodatConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {gastrodatConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    )
}

export default GastrodatConfigDetails;
